import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense } from 'react';
import { Event } from '../../types/event.types';
import { OrganizerProfile } from '../../types/organizer.types';
import { formatEventDateTime, formatEventDateForIcon, formatEventTime } from '../../utils/timezoneUtils';
import {
  BaseModal,
  Button,
  FavoriteButton,
  ShareButton,
  DirectionsButton,
  AddToCalendarButton,
  NotificationBubble,
  Icon,
  Tag,
  DateIcon,
  GlassPanel,
  LogoSVG
} from '../../pages/DesignSystem';
import { useSmartSeries } from '../../hooks/useSmartSeries';
import { groupDaysBySchedule } from '../../utils/seriesPatternUtils';
// Removed unused useFavoriteState import
import { useFavorites, useHiddenEvents } from '../../stores/appStore';
import { SwipeableCard, SwipeableCardRef } from '../../pages/DesignSystem/patterns/Modal/BaseModal';
import { getTagFallbackGradient, sortTags } from '../../pages/DesignSystem/types/tags.types';
import './EventModal.css';
import './card.css';
import { useWouterRouter } from '../../hooks/useWouterNavigation';
import { useDistance } from '../../hooks/useDistance';
import { useGeolocation } from '../../stores/locationStore';
import { SecurityUtils } from '../../utils/security';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { locationCache } from '../../services/LocationCacheService';
import { supabaseOrganizerService } from '../../supabase/organizer.service';
import { optimizeCloudinaryUrl } from '../../services/cloudinary.service';

// Lazy load MiniMap to avoid impacting bundle size
const MiniMap = React.lazy(() => import('../map/MiniMap'));

interface EventModalProps {
  event: Event;
  isOpen: boolean;
  onClose: () => void;
  onFavorite?: (eventId: string) => void;
  onDismiss?: (eventId: string) => void;
  userLocation?: {
    latitude: number;
    longitude: number;
  };
  seriesEvents?: Event[];
}

// Removed FALLBACK_IMAGE - now using LogoSVG component

const getCachedLocation = () => {
  try {
    const cached = localStorage.getItem('userLocation');
    if (cached) {
      const parsed = JSON.parse(cached);
      const isValid = Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000;
      
      if (isValid) {
        return {
          latitude: parsed.latitude,
          longitude: parsed.longitude
        };
      }
    }
    return undefined;
  } catch {
    return undefined;
  }
};

const EventModal: React.FC<EventModalProps> = ({
  event,
  isOpen,
  onClose,
  onFavorite,
  onDismiss,
  userLocation,
  seriesEvents
}) => {
  // ✅ CORRECTION : Utiliser les hooks du store
  const { toggleFavorite, isFavorite } = useFavorites();
  const { addToHiddenEvents } = useHiddenEvents();
  
  // 🚀 NOUVEAU : Fallback pour récupérer seriesEvents depuis SmartCache si pas fournis
  const [fallbackSeriesEvents, setFallbackSeriesEvents] = useState<Event[]>([]);
  
  useEffect(() => {
    const loadSeriesEvents = async () => {
      // Si seriesEvents fournis en props, pas besoin de fallback
      if (seriesEvents && seriesEvents.length > 0) {
        console.log('✅ [EventModal] SeriesEvents fournis en props, pas de fallback nécessaire');
        return;
      }
      
      // Si l'événement a un seriesId mais pas de seriesEvents, charger depuis cache
      if (event.seriesId && (!seriesEvents || seriesEvents.length === 0)) {
        console.log('🔄 [EventModal] Fallback - Chargement seriesEvents depuis SmartCache pour:', event.seriesId);

        try {
          const cachedSeriesEvents = await locationCache.getSeriesEvents(event.seriesId);
          if (cachedSeriesEvents && cachedSeriesEvents.length > 0) {
            console.log('✅ [EventModal] SeriesEvents récupérés depuis SmartCache:', cachedSeriesEvents.length);
            setFallbackSeriesEvents(cachedSeriesEvents);
          } else {
            console.log('❌ [EventModal] Aucun seriesEvents trouvé en SmartCache');
            setFallbackSeriesEvents([]);
          }
        } catch (error) {
          console.error('❌ [EventModal] Erreur chargement seriesEvents:', error);
          setFallbackSeriesEvents([]);
        }
      }
    };
    
    loadSeriesEvents();
  }, [event.seriesId, seriesEvents]);
  
  // Utiliser les seriesEvents fournis en props OU le fallback
  const effectiveSeriesEvents = useMemo(() => {
    if (seriesEvents && seriesEvents.length > 0) {
      return seriesEvents;
    }
    return fallbackSeriesEvents;
  }, [seriesEvents, fallbackSeriesEvents]);
  
  useEffect(() => {
    if (event) {
      console.log('🎯 EventModal - Event complet reçu:', {
        id: event.id,
        title: event.title,
        seriesId: event.seriesId,
        isException: event.isException,
        hasSeriesEvents: !!effectiveSeriesEvents,
        seriesEventsCount: effectiveSeriesEvents?.length || 0,
        source: (seriesEvents && seriesEvents.length > 0) ? 'props' : 'fallback'
      });
      
      if (effectiveSeriesEvents && effectiveSeriesEvents.length > 0) {
        console.log('📅 EventModal - SeriesEvents effectifs:', {
          count: effectiveSeriesEvents.length,
          events: effectiveSeriesEvents.map(e => ({
            id: e.id,
            datetime: e.datetime,
            isException: e.isException
          }))
        });
      } else {
        console.log('❌ EventModal - Aucun seriesEvents effectifs');
      }
      
      console.log('ticketingUrl:', event.ticketingUrl);
    }
  }, [event, effectiveSeriesEvents]);

  // 🏢 NOUVEAU : Chargement du profil organisateur à la volée
  useEffect(() => {
    const loadOrganizerProfile = async () => {
      if (!event.organizerId) {
        console.log('🏢 [EventModal] Pas d\'organizerId pour cet événement');
        return;
      }

      // Si on a déjà les données organisateur dans l'event, on les utilise
      if (event.organizer && event.organizerImage) {
        console.log('🏢 [EventModal] Données organisateur déjà présentes dans l\'événement');
        setOrganizerProfile({
          id: event.organizerId,
          organizer: event.organizer,
          organizerImage: event.organizerImage,
          subtitle: event.organizerSubtitle,
          organizerContact: event.organizerContact,
          // Valeurs par défaut pour les champs requis
          location: '',
          locationName: '',
          createdAt: new Date(),
          updatedAt: new Date()
        } as OrganizerProfile);
        return;
      }

      console.log('🏢 [EventModal] Chargement du profil organisateur:', event.organizerId);
      setOrganizerLoading(true);

      try {
        // 1. PRIORITÉ : Vérifier IndexedDB publicOrganizers (TTL géré par getPublicOrganizers)
        const cachedOrganizers = await locationCache.getPublicOrganizers([event.organizerId]);
        const cachedProfile = cachedOrganizers.get(event.organizerId);

        if (cachedProfile) {
          // ✅ Cache valide (TTL OK) - utiliser même si organizer est vide
          // Le display gère le fallback "Unknown Organizer" si nécessaire
          console.log('💾 [EventModal] Profil organisateur trouvé en cache:', {
            organizer: cachedProfile.organizer || '(non renseigné)',
            hasImage: !!cachedProfile.organizerImage
          });
          setOrganizerProfile(cachedProfile);
          return;
        }

        // 2. FALLBACK : Pas en cache → Charger depuis Supabase
        console.log('🔥 [EventModal] Cache miss, chargement depuis Supabase:', event.organizerId);
        const profile = await supabaseOrganizerService.getOrganizerProfile(event.organizerId);

        if (profile) {
          console.log('✅ [EventModal] Profil organisateur chargé depuis Supabase:', {
            id: profile.id,
            organizer: profile.organizer || '(non renseigné)',
            hasImage: !!profile.organizerImage
          });
          setOrganizerProfile(profile);

          // Sauvegarder en cache pour la prochaine fois (même si organizer vide)
          await locationCache.savePublicOrganizers([profile]);
          console.log('💾 [EventModal] Profil organisateur sauvegardé en cache');
        } else {
          console.log('❌ [EventModal] Profil organisateur non trouvé dans Supabase');
        }

      } catch (error) {
        console.error('❌ [EventModal] Erreur chargement profil organisateur:', error);
      } finally {
        setOrganizerLoading(false);
      }
    };

    loadOrganizerProfile();
  }, [event.organizerId, event.organizer]);

  const [imageLoaded, setImageLoaded] = useState(false);
  // 🔧 FIX PRD Fiabilité découverte (item 5) : bascule sur le fallback si l'image casse (404 Cloudinary)
  const [imageLoadError, setImageLoadError] = useState(false);
  const [showShareNotification, setShowShareNotification] = useState(false);
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  // Removed unused description expansion state
  const [isExpanded, setIsExpanded] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);

  // 🔍 LIGHTBOX INTERACTIF - State pour zoom/pan
  const [lightboxScale, setLightboxScale] = useState(1);
  const [lightboxTranslate, setLightboxTranslate] = useState({ x: 0, y: 0 });
  const lightboxImageRef = useRef<HTMLDivElement>(null);
  const pinchStartDistRef = useRef<number>(0);
  const pinchStartScaleRef = useRef<number>(1);
  const panStartRef = useRef<{ x: number; y: number } | null>(null);
  const panStartTranslateRef = useRef({ x: 0, y: 0 });
  const lastTapRef = useRef<number>(0);

  const resetLightboxTransform = useCallback(() => {
    setLightboxScale(1);
    setLightboxTranslate({ x: 0, y: 0 });
  }, []);

  const openLightbox = useCallback(() => {
    resetLightboxTransform();
    setShowLightbox(true);
  }, [resetLightboxTransform]);

  const closeLightbox = useCallback(() => {
    setShowLightbox(false);
    resetLightboxTransform();
  }, [resetLightboxTransform]);

  // Pinch-to-zoom handlers
  const handleLightboxTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch start
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchStartDistRef.current = Math.hypot(dx, dy);
      pinchStartScaleRef.current = lightboxScale;
    } else if (e.touches.length === 1 && lightboxScale > 1) {
      // Pan start (only when zoomed)
      panStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      panStartTranslateRef.current = { ...lightboxTranslate };
    }
  }, [lightboxScale, lightboxTranslate]);

  const handleLightboxTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch zoom
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const ratio = dist / pinchStartDistRef.current;
      const newScale = Math.min(Math.max(pinchStartScaleRef.current * ratio, 1), 5);
      setLightboxScale(newScale);
      // Reset translate if back to scale 1
      if (newScale <= 1) {
        setLightboxTranslate({ x: 0, y: 0 });
      }
    } else if (e.touches.length === 1 && lightboxScale > 1 && panStartRef.current) {
      // Pan
      e.preventDefault();
      const dx = e.touches[0].clientX - panStartRef.current.x;
      const dy = e.touches[0].clientY - panStartRef.current.y;
      setLightboxTranslate({
        x: panStartTranslateRef.current.x + dx,
        y: panStartTranslateRef.current.y + dy
      });
    }
  }, [lightboxScale]);

  const handleLightboxTouchEnd = useCallback((e: React.TouchEvent) => {
    // Double-tap to toggle zoom
    if (e.touches.length === 0 && e.changedTouches.length === 1) {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        // Double tap
        if (lightboxScale > 1) {
          resetLightboxTransform();
        } else {
          setLightboxScale(2.5);
        }
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
    }
    panStartRef.current = null;
  }, [lightboxScale, resetLightboxTransform]);

  // Zoom buttons
  const handleLightboxZoomIn = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setLightboxScale(prev => Math.min(prev * 1.5, 5));
  }, []);

  const handleLightboxZoomOut = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const newScale = lightboxScale / 1.5;
    if (newScale <= 1) {
      resetLightboxTransform();
    } else {
      setLightboxScale(newScale);
    }
  }, [lightboxScale, resetLightboxTransform]);

  // Download / Share
  const handleLightboxDownload = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    const imageUrl = optimizeCloudinaryUrl(event.image, { width: 1200 });

    // Try native share first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: event.title,
          url: imageUrl
        });
        return;
      } catch {
        // Cancelled or failed, fall through to download
      }
    }

    // Fallback: open in new tab (allows long-press to save on mobile)
    window.open(imageUrl, '_blank');
  }, [event.image, event.title]);

  // Détection mobile pour le comportement de l'image (lightbox vs nouvel onglet)
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

  // Handler pour ouvrir l'image en grand
  const handleExpandImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMobile) {
      openLightbox();
    } else {
      window.open(event.image, '_blank');
    }
  };
  
  // 🏢 NOUVEAU : État pour les données organisateur
  const [organizerProfile, setOrganizerProfile] = useState<OrganizerProfile | null>(null);
  const [organizerLoading, setOrganizerLoading] = useState(false);
  // Removed unused descriptionRef
  const { pattern, loading: seriesLoading, series: smartSeriesData } = useSmartSeries(event.seriesId, undefined, event.organizerId, seriesEvents);
  const daySchedules = smartSeriesData?.daySchedules;
  const hasDaySchedules = daySchedules && Object.keys(daySchedules).length > 0;
  const { navigate } = useWouterRouter();
  const { location: geoLocation } = useGeolocation();
  
  // Analytics hooks
  const analytics = useAnalytics();
  const startTime = useRef<number>(Date.now());
  const [hasScrolled, setHasScrolled] = useState(false);
  const [sectionsViewed] = useState<string[]>([]);
  const [scrollDepth, setScrollDepth] = useState(0);

  const getExpirationDate = () => {
    if (effectiveSeriesEvents && effectiveSeriesEvents.length > 0) {
      const sortedEvents = [...effectiveSeriesEvents].sort((a, b) => {
        if (!a.datetime || !b.datetime) return 0;
        return new Date(b.datetime).getTime() - new Date(a.datetime).getTime();
      });
      return sortedEvents[0].datetime ? new Date(sortedEvents[0].datetime) : new Date();
    }
    return event.datetime ? new Date(event.datetime) : new Date();
  };

  // Removed unused toggleFavorite

  const sortedEvents = useMemo(() => {
    const now = new Date();
    return effectiveSeriesEvents
      ?.filter(event => {
        if (!event.datetime) return false;
        const eventDate = new Date(event.datetime);
        return eventDate > now;
      })
      .sort((a, b) => {
        if (!a.datetime || !b.datetime) return 0;
        const dateA = new Date(a.datetime);
        const dateB = new Date(b.datetime);
        return dateA.getTime() - dateB.getTime();
      }) || [];
  }, [effectiveSeriesEvents]);

  // Simplifier le pattern de récurrence (comme RecurringEventCard)
  const simplifiedPattern = useMemo(() => {
    if (!pattern) return 'Recurring';

    const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const mentionedDays = allDays.filter(day =>
      new RegExp(`\\b${day}\\b`, 'i').test(pattern)
    );

    const untilMatch = pattern.match(/\s*(until\s+.+)$/i);
    const untilPart = untilMatch ? ` ${untilMatch[1]}` : '';

    // Gérer les patterns "Every day" déjà formatés par useSmartSeries
    if (/^Every\s+day\b/i.test(pattern)) {
      let dayPart = pattern.replace(/\s+at\s+\d{1,2}:\d{2}.*/i, '');
      dayPart = dayPart.replace(/\s+until\s+.+$/i, '').trim();
      // Strip daySchedules parenthetical
      dayPart = dayPart.replace(/\s*\([^)]*\d{1,2}:\d{2}[^)]*\)/g, '');
      return `${dayPart}${untilPart}`;
    }

    if (mentionedDays.length >= 5) {
      const missingDays = allDays.filter(day => !mentionedDays.includes(day));
      if (missingDays.length === 0) {
        return `Every day${untilPart}`;
      } else if (missingDays.length === 1) {
        return `Every day except ${missingDays[0]}${untilPart}`;
      } else {
        const lastDay = missingDays.pop();
        return `Every day except ${missingDays.join(', ')} and ${lastDay}${untilPart}`;
      }
    }

    let simplified = pattern;
    // Strip daySchedules parenthetical (e.g., "(Tue, Thu 09:00 - 10:00 · Sat 04:00)")
    simplified = simplified.replace(/\s*\([^)]*\d{1,2}:\d{2}[^)]*\)/g, '');
    simplified = simplified.replace(/\s*[-–]\s*\d{1,2}:\d{2}/gi, '');
    simplified = simplified.replace(/\s+at\s+\d{1,2}:\d{2}(\s*(AM|PM))?/gi, '');
    simplified = simplified.replace(/\s+/g, ' ').trim();
    return simplified || 'Recurring';
  }, [pattern]);

  // Formater le pattern avec les jours en gras
  const formattedPattern = useMemo(() => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    // Nettoyer les <strong> existants pour re-formater uniformément
    let result = simplifiedPattern.replace(/<\/?strong>/g, '');

    // Mettre "Every day" en gras
    result = result.replace(/\bEvery day\b/g, '<strong>Every day</strong>');

    // Mettre "except" en gras
    result = result.replace(/\bexcept\b/gi, '<strong>except</strong>');

    days.forEach(day => {
      const regex = new RegExp(`\\b${day}\\b`, 'gi');
      result = result.replace(regex, `<strong>${day}</strong>`);
    });
    return result;
  }, [simplifiedPattern]);

  // Obtenir les heures de l'événement — approche adaptative pour horaires variables
  // Fallback: déduit les horaires depuis les occurrences si daySchedules est absent
  const eventTime = useMemo(() => {
    const formatGroups = (groups: Array<{ days: string[]; start: string; end: string }>) => {
      if (groups.length === 1) {
        return `${groups[0].start}${groups[0].end ? ` - ${groups[0].end}` : ''}`;
      } else if (groups.length <= 3) {
        return groups.map(g => {
          const days = g.days.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ');
          return `${days} ${g.start}${g.end ? ` - ${g.end}` : ''}`;
        }).join(' · ');
      } else {
        return 'Various times';
      }
    };

    // Path 1: daySchedules from series
    if (hasDaySchedules && daySchedules) {
      return formatGroups(groupDaysBySchedule(daySchedules));
    }

    // Path 2: Deduce from occurrences (legacy events)
    if (sortedEvents.length >= 2) {
      const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const timesByDay = new Map<string, { start: string; end: string }>();

      for (const evt of sortedEvents) {
        if (!evt.datetime) continue;
        const d = new Date(evt.datetime);
        const dayName = DAY_NAMES[d.getDay()];
        if (timesByDay.has(dayName)) continue;
        const start = formatEventTime(evt.datetime, evt.timezone);
        const end = evt.endTime ? formatEventTime(evt.endTime, evt.timezone) : '';
        timesByDay.set(dayName, { start, end });
      }

      const uniqueTimes = new Set(Array.from(timesByDay.values()).map(v => `${v.start}|${v.end}`));
      if (uniqueTimes.size > 1) {
        const scheduleRecord: Record<string, { start: string; end: string }> = {};
        timesByDay.forEach((v, k) => { scheduleRecord[k] = v; });
        return formatGroups(groupDaysBySchedule(scheduleRecord));
      }
    }

    return event.datetime
      ? `${formatEventTime(event.datetime, event.timezone)}${event.endTime ? ` - ${formatEventTime(event.endTime, event.timezone)}` : ''}`
      : '';
  }, [hasDaySchedules, daySchedules, sortedEvents, event.datetime, event.endTime, event.timezone]);

  // Affichage de l'horaire pour une occurrence dans la liste dépliée.
  // Exceptions: lit datetime/endTime de l'event (sa vérité custom).
  // Sinon avec daySchedules: lit le créneau du jour en TZ de l'event (pas du navigateur).
  const getOccurrenceTimeDisplay = useCallback((evt: Event): string => {
    const fromEventTimes = (): string => {
      if (!evt.datetime) return '';
      const start = formatEventTime(evt.datetime, evt.timezone);
      const end = evt.endTime ? formatEventTime(evt.endTime, evt.timezone) : '';
      return end ? `${start} - ${end}` : start;
    };

    if (evt.isException) return fromEventTimes();

    if (hasDaySchedules && daySchedules && evt.datetime) {
      const dayName = formatEventDateTime(evt.datetime, evt.timezone).weekday.toLowerCase();
      const schedule = daySchedules[dayName];
      if (schedule?.start) {
        return schedule.end ? `${schedule.start} - ${schedule.end}` : schedule.start;
      }
    }

    return fromEventTimes();
  }, [hasDaySchedules, daySchedules]);

  // Déterminer le statut d'une occurrence
  const getOccurrenceStatus = (evt: Event) => {
    if (evt.isException) return { label: 'Modified', className: 'modified' };
    return { label: 'Upcoming', className: 'upcoming' };
  };

  // Removed unused expand button logic

  const effectiveLocation = userLocation ?? getCachedLocation() ?? geoLocation;
  const distance = useDistance(effectiveLocation, {
    latitude: event.latitude,
    longitude: event.longitude
  });

  const swipeableCardRef = useRef<SwipeableCardRef>(null);

  // 📱 Swipe-down-to-close pour les iPhones (pas de bouton retour)
  // Utilise des event listeners natifs car React enregistre les touch events
  // comme passifs, ce qui empêche preventDefault() de fonctionner
  const modalDragRef = useRef({
    isDragging: false,
    startY: 0,
    currentY: 0,
    velocity: 0,
    lastTime: 0
  });
  const modalContentRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const el = modalContentRef.current;
    if (!el || !isOpen) return;

    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      const scrollContainer = target.closest('.event-modal');
      if (scrollContainer && scrollContainer.scrollTop > 5) return;

      const touch = e.touches[0];
      const drag = modalDragRef.current;
      drag.isDragging = true;
      drag.startY = touch.clientY;
      drag.currentY = touch.clientY;
      drag.velocity = 0;
      drag.lastTime = Date.now();
    };

    const handleTouchMove = (e: TouchEvent) => {
      const drag = modalDragRef.current;
      if (!drag.isDragging) return;

      const target = e.target as HTMLElement;
      const scrollContainer = target.closest('.event-modal');
      if (scrollContainer && scrollContainer.scrollTop > 5) {
        drag.isDragging = false;
        el.style.transform = '';
        el.style.transition = '';
        return;
      }

      const touch = e.touches[0];
      const currentTime = Date.now();
      const deltaTime = Math.max(1, currentTime - drag.lastTime);
      const deltaY = touch.clientY - drag.currentY;
      drag.velocity = deltaY / deltaTime;

      const totalDeltaY = touch.clientY - drag.startY;

      if (totalDeltaY > 0) {
        e.preventDefault(); // Fonctionne car listener natif non-passif
        el.style.transition = 'none';
        el.style.transform = `translateY(${totalDeltaY}px)`;
        const opacity = Math.max(0.5, 1 - totalDeltaY / 400);
        el.style.opacity = `${opacity}`;
      }

      drag.currentY = touch.clientY;
      drag.lastTime = currentTime;
    };

    const handleTouchEnd = () => {
      const drag = modalDragRef.current;
      if (!drag.isDragging) return;

      const totalDeltaY = drag.currentY - drag.startY;
      const shouldClose = totalDeltaY > 100 || drag.velocity > 0.5;

      drag.isDragging = false;
      drag.velocity = 0;

      if (shouldClose) {
        el.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
        el.style.transform = 'translateY(100vh)';
        el.style.opacity = '0';
        setTimeout(() => {
          onCloseRef.current();
          el.style.transform = '';
          el.style.opacity = '';
          el.style.transition = '';
        }, 300);
      } else {
        el.style.transition = 'transform 0.3s ease-out, opacity 0.3s ease-out';
        el.style.transform = '';
        el.style.opacity = '';
      }
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false }); // non-passif pour preventDefault
    el.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isOpen]);

  // Removed unused handleFavoriteClick

  // Analytics handlers
  const handleScroll = (e: React.UIEvent) => {
    setHasScrolled(true);
    const target = e.target as HTMLElement;
    const scrollPercent = (target.scrollTop / (target.scrollHeight - target.clientHeight)) * 100;
    setScrollDepth(Math.max(scrollDepth, scrollPercent));
  };

  // ✅ SIMPLIFIÉ : Gestionnaire de fermeture avec protection contre les doubles appels + Analytics
  const [isClosing, setIsClosing] = useState(false);
  
  const handleClose = () => {
    if (isClosing) {
      console.log('🚫 EventModal - Fermeture déjà en cours, ignorée');
      return;
    }
    
    console.log('🔒 EventModal - Fermeture demandée');
    setIsClosing(true);
    
    // Analytics tracking
    const consultationDuration = Date.now() - startTime.current;
    analytics.trackEventConsultation(event, consultationDuration, {
      hasScrolled,
      sectionsViewed,
      actionsPerformed: [], // À enrichir selon les actions
      scrollDepth
    });
    
    onClose();
  };

  // ✅ SIMPLIFIÉ : Gestionnaire normal pour les clics interactifs
  const handleInteractiveClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation(); // Juste empêcher la propagation, sans preventDefault
    action();
  };

  const hasImage = Boolean(event.image) && !imageLoadError;

  // 🔧 FIX PRD Fiabilité découverte (item 5) : réinitialiser l'état d'erreur quand l'image change
  // (le composant peut réutiliser la même instance en naviguant entre events d'une série)
  useEffect(() => {
    setImageLoadError(false);
  }, [event.image]);

  // Dégradé de fond basé sur les tags (events sans image)
  const fallbackGradient = useMemo(() => getTagFallbackGradient(event.tags), [event.tags]);

  // Rendu des tags
  const renderTags = () => (
    <div className="event-card__tags">
      {sortTags(event.tags).map(tag => (
        <Tag
          key={tag}
          label={tag}
          type="activity"
          size="md"
        />
      ))}
    </div>
  );

  // Rendu du header (tags + actions)
  const renderHeader = () => (
    <div className="event-card__header">
      {renderTags()}
      <div className="event-card__actions" onClick={e => e.stopPropagation()}>
        <ShareButton
          shareData={{
            title: event.title,
            text: `what2do.today\nCome discover ${event.title}`,
            url: window.location.href
          }}
          event={event}
          analyticsContext="modale"
          variant="icon"
          size="lg"
          shouldTriggerSuccess={false}
          onShareSuccess={() => setShowShareNotification(true)}
        />
        <FavoriteButton
          id={event.id}
          seriesId={event.seriesId}
          organizerId={event.organizerId}
          expiresAt={getExpirationDate()}
          fullEvent={event}
          onFavoriteChange={(newValue) => {
            console.log('FavoriteButton onFavoriteChange', newValue);
          }}
          size="lg"
          analyticsContext="modale"
        />
      </div>
    </div>
  );

  // Rendu du prix - triés par ordre croissant, un par ligne
  const renderPriceText = () => {
    // No prices at all → show nothing
    if (!event.prices || event.prices.length === 0) {
      return null;
    }

    // Check if all prices have amount === 0 → Free
    const allFree = event.prices.every(price => price.amount === 0);
    if (allFree) {
      return <span className="event-card__glass-price event-card__glass-price--free">Free</span>;
    }

    // Trier par prix croissant, null amounts last
    const sortedPrices = [...event.prices].sort((a, b) => {
      if (a.amount === null && b.amount === null) return 0;
      if (a.amount === null) return 1;
      if (b.amount === null) return -1;
      return a.amount - b.amount;
    });

    return (
      <div className="event-card__glass-prices">
        {sortedPrices.map((price, index) => (
          <span key={index} className={`event-card__glass-price-line${price.amount === null ? ' event-card__glass-price--info' : ''}`}>
            {price.amount === null
              ? (price.description || 'Contact organizer')
              : price.amount === 0
                ? 'Free'
                : `${price.amount}${price.currency === 'EUR' ? '€' : price.currency}`}
            {price.amount !== null && price.description && ` ${price.description}`}
          </span>
        ))}
      </div>
    );
  };

  // Vérifier si l'event est annulé
  const isCancelled = event.status === 'cancelled';

  // GlassPanel principal avec les infos (identique aux cards)
  const renderMainGlassPanel = () => (
    <div className="event-card__glass-container">
      <GlassPanel variant="light" blur="md" radius="md">
        <div className="event-card__glass-content">
          {/* Badge Annulé si l'event est cancelled */}
          {isCancelled && (
            <span className="event-card__cancelled-badge">Cancelled</span>
          )}
          {/* Titre */}
          <h3 className={`event-card__glass-title ${isCancelled ? 'event-card__glass-title--cancelled' : ''}`}>{event.title}</h3>

          {/* Date/Pattern + heures */}
          {event.seriesId ? (
            <div className="event-card__glass-row">
              <div className="event-card__glass-icon">
                <Icon name="Repeat" size={20} />
              </div>
              <div className="event-card__glass-location-row">
                <div className="event-card__glass-info">
                  <span
                    className="event-card__glass-primary"
                    dangerouslySetInnerHTML={{ __html: SecurityUtils.sanitizeHtml(seriesLoading ? 'Loading...' : formattedPattern) }}
                  />
                  <span className="event-card__glass-secondary">
                    {eventTime}
                  </span>
                </div>
                {sortedEvents.length > 0 && (
                  <Button
                    variant="glass"
                    onClick={(e) => handleInteractiveClick(e, () => setIsExpanded(!isExpanded))}
                    aria-label={isExpanded ? "Hide dates" : "Show dates"}
                  >
                    <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
                  </Button>
                )}
              </div>
            </div>
          ) : (
            event.datetime && (
              <div className="event-card__glass-row">
                <div className="event-card__glass-icon">
                  <DateIcon
                    {...formatEventDateForIcon(event.datetime, event.timezone)}
                    variant="dark"
                    size="sm"
                  />
                </div>
                <div className="event-card__glass-location-row">
                  <div className="event-card__glass-info">
                    <span className="event-card__glass-primary">
                      <strong>{formatEventDateTime(event.datetime, event.timezone).weekday}</strong>
                    </span>
                    <span className="event-card__glass-secondary">
                      {eventTime}
                    </span>
                  </div>
                  <AddToCalendarButton
                    event={event}
                    analyticsContext="modale"
                  />
                </div>
              </div>
            )
          )}

          {/* Liste des dates récurrentes (dropdown) */}
          {event.seriesId && sortedEvents.length > 0 && (
            <div
              className={`recurring-card__dates-list ${isExpanded ? 'recurring-card__dates-list--expanded' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              {sortedEvents.map((evt) => {
                const status = getOccurrenceStatus(evt);
                return (
                  <div key={evt.id} className="recurring-card__date-item">
                    <div className="recurring-card__date-info">
                      <span className="recurring-card__date">
                        {formatEventDateTime(evt.datetime, evt.timezone).date}
                      </span>
                      <span className="recurring-card__date-time">
                        {getOccurrenceTimeDisplay(evt)}
                      </span>
                    </div>
                    <div className="recurring-card__date-right">
                      <span className={`recurring-card__status-text recurring-card__status-text--${status.className}`}>
                        {status.label}
                      </span>
                      <AddToCalendarButton
                        event={evt}
                        analyticsContext="modale"
                        iconSize={14}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Lieu + distance + navigation */}
          <div className="event-card__glass-row">
            <div className="event-card__glass-icon">
              <Icon name="MapPin" size={20} />
            </div>
            <div className="event-card__glass-location-row">
              <div className="event-card__glass-location-text">
                <span className="event-card__glass-location-name">
                  {event.locationName || event.location}
                </span>
                {distance && (
                  <span className="event-card__glass-location-distance">
                    · {distance}
                  </span>
                )}
              </div>
              {event.latitude && event.longitude && (
                <DirectionsButton
                  latitude={event.latitude}
                  longitude={event.longitude}
                  locationName={event.locationName || event.location || 'Event'}
                  event={event}
                  analyticsContext="modale"
                />
              )}
            </div>
          </div>

          {/* Prix */}
          {renderPriceText() && (
            <div className="event-card__glass-row">
              <div className="event-card__glass-icon">
                <Icon name="Ticket" size={20} />
              </div>
              <div className="event-card__glass-info">
                {renderPriceText()}
              </div>
            </div>
          )}

          {/* Réservation (si présent) */}
          {event.ticketingUrl && (() => {
            const value = event.ticketingUrl;

            // Détecter les liens wa.me (ex: https://wa.me/94771054107)
            const waMeMatch = value.match(/wa\.me\/(\d+)/);
            const isWhatsAppLink = !!waMeMatch;
            const waNumber = waMeMatch ? waMeMatch[1] : '';

            const isUrl = !isWhatsAppLink && (value.startsWith('http') || value.startsWith('www.'));
            const isEmail = value.includes('@') && !isUrl && !isWhatsAppLink;
            const isPhone = !isWhatsAppLink && !isUrl && !isEmail && /^[\d\s\-+().]{7,}$/.test(value.replace(/\s/g, ''));
            const iconName = (isPhone || isWhatsAppLink) ? 'MessageCircle' : isUrl ? 'ExternalLink' : isEmail ? 'Mail' : 'Info';

            // Pour les numéros de téléphone, ouvrir WhatsApp
            const getPhoneWhatsAppUrl = (phone: string) => {
              const cleaned = phone.replace(/[\s\-().]/g, '');
              return `https://wa.me/${cleaned.startsWith('+') ? cleaned.slice(1) : cleaned}`;
            };

            // Formater un numéro pour l'affichage (ex: 94771054107 → +94 77 105 4107)
            const formatPhoneDisplay = (num: string) => {
              return `+${num}`;
            };

            // Pour les URLs, afficher une version lisible
            // Instagram/Facebook/TikTok → @handle, sinon le domaine
            const getDisplayUrl = (url: string) => {
              try {
                const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
                const hostname = parsed.hostname.replace(/^www\./, '');
                const path = parsed.pathname.replace(/^\/|\/$/g, '');

                // Réseaux sociaux → afficher @handle
                if ((hostname === 'instagram.com' || hostname === 'facebook.com' || hostname === 'tiktok.com') && path) {
                  const handle = path.split('/')[0];
                  if (handle) return `@${handle}`;
                }

                return hostname;
              } catch {
                return url;
              }
            };

            return (
              <div className="event-card__glass-row">
                <div className="event-card__glass-icon">
                  <Icon name={iconName} size={20} />
                </div>
                <div className="event-card__glass-info">
                  {isWhatsAppLink ? (
                    <a
                      href={value.startsWith('http') ? value : `https://${value}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="event-card__glass-primary event-card__glass-primary--link"
                    >
                      {formatPhoneDisplay(waNumber)}
                    </a>
                  ) : isUrl ? (
                    <a
                      href={value.startsWith('http') ? value : `https://${value}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="event-card__glass-primary event-card__glass-primary--link"
                    >
                      {getDisplayUrl(value)}
                    </a>
                  ) : isEmail ? (
                    <a href={`mailto:${value}`} className="event-card__glass-primary event-card__glass-primary--link">
                      {value}
                    </a>
                  ) : isPhone ? (
                    <a
                      href={getPhoneWhatsAppUrl(value)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="event-card__glass-primary event-card__glass-primary--link"
                    >
                      {value}
                    </a>
                  ) : (
                    <span className="event-card__glass-primary">{value}</span>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </GlassPanel>
    </div>
  );

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} animationStyle="scale">
      <SwipeableCard
        ref={swipeableCardRef}
        isFirstCard={true}
        swipeContext="modal"
        onLike={async () => {
          const storageId = event.seriesId || event.id;

          // ✅ Si déjà en favori, ne rien faire (évite le toggle involontaire)
          if (isFavorite(storageId)) {
            console.log('🧪 [EventModal] onLike ignoré - déjà en favori:', storageId);
            onClose();
            return;
          }

          console.log('🧪 [EventModal] SwipeCard onLike début:', { eventId: event.id, seriesId: event.seriesId, storageId });

          // ✅ CENTRALISÉ : toggleFavorite gère maintenant favoris + hiddenEvents
          await toggleFavorite(storageId, getExpirationDate(), undefined, event);
          console.log('🧪 [EventModal] toggleFavorite terminé (gère favoris + hiddenEvents)');

          if (onFavorite) {
            onFavorite(storageId);
          }
          onClose();
        }}
        onDismiss={async () => {
          console.log('🧪 [EventModal] SwipeCard onDismiss début:', { eventId: event.id, seriesId: event.seriesId, storageId: event.seriesId || event.id });

          // ✅ CORRECTION : Ajouter à hiddenEvents avec raison 'dismiss'
          await addToHiddenEvents(event.seriesId || event.id, 'dismiss');
          console.log('🧪 [EventModal] addToHiddenEvents terminé avec reason: dismiss');

          if (onDismiss) {
            onDismiss(event.seriesId || event.id);
          }
          onClose();
        }}
        eventId={event.id}
        seriesId={event.seriesId}
        organizerId={event.organizerId}
      >
        <div
          ref={modalContentRef}
          className={`event-modal ${hasImage ? 'event-modal--with-image' : 'event-modal--fallback'}`}
          onScroll={handleScroll}
        >
          {/* Drag handle visuel pour indiquer le swipe-down */}
          <div className="event-modal__drag-handle" aria-hidden="true">
            <div className="event-modal__drag-handle-bar" />
          </div>
          {/* ========== IMAGE DE FOND AU NIVEAU MODAL ========== */}
          <div
            className={`event-modal__background ${!hasImage ? 'event-modal__background--fallback' : ''}`}
            style={!hasImage && fallbackGradient ? { background: fallbackGradient } : undefined}
          >
            {hasImage ? (
              <img
                src={optimizeCloudinaryUrl(event.image, { width: 800 })}
                alt={event.title}
                className={imageLoaded ? 'loaded' : ''}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageLoadError(true)}
              />
            ) : (
              <LogoSVG
                variant="vertTexte"
                size="auto"
                padding={0}
              />
            )}
          </div>

          {/* ========== PARTIE HAUTE : Structure identique aux cards ========== */}
          <div className={`event-modal__card-section ${hasImage ? 'event-modal__card-section--with-image' : 'event-modal__card-section--fallback'}`}>
            {/* Header avec tags et actions */}
            {renderHeader()}

            {/* Spacer pour pousser le GlassPanel vers le bas (comme RecurringEventCard) */}
            <div className="event-card__spacer">
              {/* Bouton pour agrandir l'image - uniquement si image présente */}
              {hasImage && (
                <button
                  className="event-modal__expand-image"
                  onClick={handleExpandImage}
                  aria-label="Agrandir l'image"
                >
                  <Icon name="Fullscreen" size={20} />
                </button>
              )}
            </div>

            {/* GlassPanel avec les infos principales */}
            {renderMainGlassPanel()}
          </div>

          {/* ========== MINI-MAP : Localisation visuelle ========== */}
          {event.latitude && event.longitude && (
            <div className="event-modal__map-section">
              <Suspense fallback={<div className="mini-map--loading" />}>
                <MiniMap
                  eventLocation={{ latitude: event.latitude, longitude: event.longitude }}
                  eventName={event.locationName || event.location || event.title}
                  userLocation={effectiveLocation}
                />
              </Suspense>
            </div>
          )}

          {/* ========== PARTIE BASSE : Contenu détaillé ========== */}
          <div className="event-modal__details">
            {/* Bloc organisateur + description + signaler */}
            <GlassPanel variant="light" blur="md" radius="md">
              {/* Organisateur (cliquable) */}
              {(organizerProfile || organizerLoading || event.organizer) && (
                <div
                  className="event-modal-organizer-block"
                  onClick={(e) => handleInteractiveClick(e, () => event.organizerId && navigate(`/organizer-profile/${event.organizerId}`))}
                >
                  <div className="modal-item-content">
                    <div className="modal-item-icon">
                      {organizerLoading ? (
                        <div className="event-modal-organizer-avatar-loading">
                          <Icon name="Loader" size={24} />
                        </div>
                      ) : (
                        <>
                          {(organizerProfile?.organizerImage || event.organizerImage) ? (
                            <img
                              src={optimizeCloudinaryUrl(organizerProfile?.organizerImage || event.organizerImage, { width: 100 })}
                              alt={organizerProfile?.organizer || event.organizer || 'Organizer'}
                              className="event-modal-organizer-avatar"
                            />
                          ) : (
                            <div className="event-modal-organizer-avatar-fallback">
                              {(organizerProfile?.organizer || event.organizer || '?').charAt(0).toUpperCase()}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="event-modal-organizer-info">
                      {organizerLoading ? (
                        <span className="event-modal-organizer-name">
                          Loading organizer...
                        </span>
                      ) : (
                        <>
                          <span className="event-modal-organizer-name">
                            {organizerProfile?.organizer || event.organizer || 'Unknown Organizer'}
                          </span>
                          {(organizerProfile?.subtitle || event.organizerSubtitle) && (
                            <span className="event-modal-organizer-subtitle">
                              {organizerProfile?.subtitle || event.organizerSubtitle}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <Icon name="ChevronRight" size={20} className="event-modal-organizer-chevron" />
                  </div>
                </div>
              )}

              {/* Description */}
              {event.description && (
                <div className="modal-description-container">
                  <p className="modal-description">
                    {event.description}
                  </p>
                </div>
              )}

              {/* Bouton Signaler */}
              <div className="event-modal__report-container">
                <Button
                  variant="ghost"
                  size="sm"
                  className="event-modal__report-button"
                  onClick={(e) => handleInteractiveClick(e, () => {
                    // TODO: Implémenter la logique de signalement
                    console.log('Report event:', event.id);
                  })}
                >
                  <Icon name="Flag" size={16} />
                  <span>Report this event</span>
                </Button>
              </div>
            </GlassPanel>
          </div>
        </div>
      </SwipeableCard>

      <NotificationBubble
        show={showShareNotification}
        message="Link copied! ✨"
        type="success"
        duration={1500}
        onHide={() => setShowShareNotification(false)}
      />

      <NotificationBubble
        show={showCopyNotification}
        message="Contact copied! ✨"
        type="success"
        duration={1500}
        onHide={() => setShowCopyNotification(false)}
      />

      {/* Lightbox interactif - pinch-to-zoom, pan, download (mobile) */}
      {showLightbox && hasImage && (
        <div
          className="event-modal__lightbox"
          onClick={() => { if (lightboxScale <= 1) closeLightbox(); }}
        >
          {/* Toolbar en haut */}
          <div className="event-modal__lightbox-toolbar">
            <button
              className="event-modal__lightbox-btn"
              onClick={handleLightboxDownload}
              aria-label="Save image"
            >
              <Icon name="Download" size={22} />
            </button>
            <button
              className="event-modal__lightbox-btn"
              onClick={closeLightbox}
              aria-label="Close"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {/* Zone image zoomable */}
          <div
            ref={lightboxImageRef}
            className="event-modal__lightbox-container"
            onTouchStart={handleLightboxTouchStart}
            onTouchMove={handleLightboxTouchMove}
            onTouchEnd={handleLightboxTouchEnd}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={optimizeCloudinaryUrl(event.image, { width: 1200 })}
              alt={event.title}
              className="event-modal__lightbox-image"
              draggable={false}
              style={{
                transform: `scale(${lightboxScale}) translate(${lightboxTranslate.x / lightboxScale}px, ${lightboxTranslate.y / lightboxScale}px)`,
                transition: lightboxScale === 1 ? 'transform 0.2s ease-out' : 'none'
              }}
            />
          </div>

          {/* Contrôles zoom en bas */}
          <div className="event-modal__lightbox-controls">
            <button
              className="event-modal__lightbox-btn"
              onClick={handleLightboxZoomOut}
              disabled={lightboxScale <= 1}
              aria-label="Zoom out"
            >
              <Icon name="ZoomOut" size={22} />
            </button>
            <span className="event-modal__lightbox-zoom-level">
              {Math.round(lightboxScale * 100)}%
            </span>
            <button
              className="event-modal__lightbox-btn"
              onClick={handleLightboxZoomIn}
              disabled={lightboxScale >= 5}
              aria-label="Zoom in"
            >
              <Icon name="ZoomIn" size={22} />
            </button>
          </div>
        </div>
      )}
    </BaseModal>
  );
};

export default EventModal;
